import React, { useMemo, useState } from "react";
import {
  Tabs,
  Table,
  Tag,
  Button,
  Drawer,
  Descriptions,
  Space,
  Badge,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import AuthUser from "../../../../components/AuthUser";
import { toast } from "react-toastify";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const faseTag = (label) => {
  switch (label) {
    case "Recebida":
      return <Tag>Recebida</Tag>;
    case "Triagem":
      return <Tag color="blue">Triagem</Tag>;
    case "Entrevista":
      return <Tag color="geekblue">Entrevista</Tag>;
    case "Documentos":
      return <Tag color="purple">Documentos</Tag>;
    case "Aprovada":
      return <Tag color="green">Aprovada</Tag>;
    case "Reprovada":
      return <Tag color="red">Reprovada</Tag>;
    default:
      return <Tag>{label}</Tag>;
  }
};

const nextEstado = (n) => Math.min(Number(n) + 1, 6);
const prevEstado = (n) => Math.max(Number(n) - 1, 1);



export const exportCandidatoPDF = (cand) => {
  const doc = new jsPDF("p", "pt", "a4");

  // LOGO
  doc.addImage(
    window.location.origin + "/assets/alvjamba-logo.png",
    "PNG",
    40,
    30,
    50,
    50
  );

  // NIF
  doc.setFontSize(10);
  doc.text("NIF: 5403089950", 400, 45);

  // TÍTULO
  doc.setFontSize(16);
  doc.text("FICHA DE CANDIDATURA", 40, 110);

  doc.setFontSize(11);
  doc.text(`Estado atual: ${cand.status}`, 40, 130);

  // DADOS PRINCIPAIS
  autoTable(doc, {
    startY: 150,
    head: [["Campo", "Valor"]],
    body: [
      ["Nome", cand.nome],
      ["E-mail", cand.email],
      ["Telefone", cand.telefone],
      ["Província", cand.provincia],
      ["Cargo Pretendido", cand.cargoPretendido],
      ["Disponibilidade", cand.disponibilidade],
      ["Data de Submissão", cand.dataCandidatura],
      ["Estado", cand.status],
    ],
    styles: { fontSize: 10 },
    headStyles: { fillColor: [230, 230, 230] },
  });

  // FORMAÇÃO
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [["Formação", "Detalhes"]],
    body: [
      ["Nível Académico", cand.nivelAcademico || "-"],
      ["Curso", cand.curso || "-"],
      ["Instituição", cand.escola || "-"],
      ["Período", `${cand.inicioCurso || "-"} a ${cand.fimCurso || "-"}`],
    ],
  });

  // EXPERIÊNCIA
  const y = doc.lastAutoTable.finalY + 30;
  doc.setFontSize(12);
  doc.text("Experiência Profissional", 40, y);

  doc.setFontSize(10);
  (cand.experiencia || []).forEach((e, i) => {
    doc.text(`• ${e}`, 40, y + 15 + i * 14);
  });

  // OBSERVAÇÕES
  if (cand.observacoes) {
    doc.addPage();
    doc.setFontSize(12);
    doc.text("Observações", 40, 50);
    doc.setFontSize(10);
    doc.text(cand.observacoes, 40, 70, { maxWidth: 500 });
  }

  doc.save(`Candidatura_${cand.nome.replace(/\s+/g, "_")}.pdf`);
};

const columnsFactory = (onView, onPrev, onNext, onApprove, onReject) => [
  {
    title: "Nome",
    dataIndex: "nome",
    sorter: (a, b) => a.nome.localeCompare(b.nome),
    render: (text, record) => (
      <Space direction="vertical" size={0}>
        <span className="fw-semibold">{text}</span>
        <span style={{ fontSize: 12, color: "#666" }}>{record.email}</span>
      </Space>
    ),
  },
  { title: "Província", dataIndex: "provincia" },
  { title: "Cargo", dataIndex: "cargoPretendido" },
  {
    title: "Contacto",
    dataIndex: "telefone",
    render: (v) => <span style={{ whiteSpace: "nowrap" }}>{v}</span>,
  },
  { title: "Candidatura", dataIndex: "dataCandidatura" },
  {
    title: "Docs",
    dataIndex: "docsCount",
    render: (v) => <Badge count={v || 0} />,
  },
  {
    title: "Fase",
    dataIndex: "status",
    render: (s) => faseTag(s),
  },
  {
    title: "Ações",
    key: "actions",
    render: (_, record) => (
      <Space wrap>
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onView(record)}
        >
          Ver
        </Button>

        <Tooltip title="Recuar fase">
          <Button size="small" onClick={() => onPrev(record)}>
            ←
          </Button>
        </Tooltip>

        <Tooltip title="Mover para a próxima fase">
          <Button
            size="small"
            onClick={() => onNext(record)}
            icon={<ArrowRightOutlined />}
          >
            Próx.
          </Button>
        </Tooltip>

        <Tooltip title="Aprovar">
          <Button
            size="small"
            type="primary"
            onClick={() => onApprove(record)}
            icon={<CheckCircleOutlined />}
          >
            Aprovar
          </Button>
        </Tooltip>

        <Tooltip title="Reprovar">
          <Button
            size="small"
            danger
            onClick={() => onReject(record)}
            icon={<CloseCircleOutlined />}
          >
            Reprovar
          </Button>
        </Tooltip>
      </Space>
    ),
  },
];

const DetalheDrawer = ({
  open,
  onClose,
  cand,
  anexos,
  onDownload,
  onExportPdf,
}) => (
  <Drawer
    title={cand ? `Candidato: ${cand.nome}` : "Detalhes"}
    width={560}
    open={open}
    onClose={onClose}
    destroyOnClose
  >
    {!cand ? null : (
      <>
        <Descriptions bordered size="middle" column={1}>
          <Descriptions.Item label="Nome">{cand.nome}</Descriptions.Item>
          <Descriptions.Item label="E-mail">{cand.email}</Descriptions.Item>
          <Descriptions.Item label="Telefone">
            {cand.telefone}
          </Descriptions.Item>
          <Descriptions.Item label="Província">
            {cand.provincia}
          </Descriptions.Item>
          <Descriptions.Item label="Cargo pretendido">
            {cand.cargoPretendido}
          </Descriptions.Item>
          <Descriptions.Item label="Disponibilidade">
            {cand.disponibilidade}
          </Descriptions.Item>
          <Descriptions.Item label="Data da candidatura">
            {cand.dataCandidatura}
          </Descriptions.Item>
          <Descriptions.Item label="Fase">
            {faseTag(cand.status)}
          </Descriptions.Item>
        </Descriptions>

        <div className="mt-3" />

        <Descriptions title="Documentos" bordered size="small" column={1}>
          <Descriptions.Item label="Total">
            {anexos?.length || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Lista">
            <div className="d-flex flex-column gap-2">
              {(anexos || []).map((a) => (
                <div
                  key={a.id}
                  className="d-flex align-items-center justify-content-between"
                >
                  <span>{a.ficheiro_nome}</span>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => onDownload(a)}
                  >
                    Download
                  </Button>
                </div>
              ))}
              {!anexos || anexos.length === 0 ? (
                <span style={{ color: "#999" }}>Sem anexos.</span>
              ) : null}
            </div>
          </Descriptions.Item>
        </Descriptions>

        <div className="mt-3" />

        <Button
          type="primary"
          icon={<FileTextOutlined />}
          onClick={() => onExportPdf?.(cand)}
        >
          Exportar PDF
        </Button>
      </>
    )}
  </Drawer>
);

export default function CandidaturaTabs({ data, onUpdateEstado, onRefresh }) {
  const { http } = AuthUser();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [anexos, setAnexos] = useState([]);

  const onView = async (row) => {
    setSelected(row);
    setDrawerOpen(true);

    try {
      const res = await http.get(`/candidatos/${row.id}/anexos`);
      setAnexos(res.data.data || []);
    } catch (e) {
      console.error(e);
      setAnexos([]);
      console.log("Erro ao carregar anexos.");
    }
  };

  const onExportPdf = async (cand) => {
    try {
      const res = await http.get(`/candidatos/${cand.id}`);

      const full = {
        ...res.data,
        status: res.data.statusLabel,
        dataCandidatura: res.data.dataCandidatura,
        experiencia: (res.data.experienciaRaw || "").split("\n").filter(Boolean),
      };

      exportCandidatoPDF(full);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar PDF do candidato.\n" + (e?.message || ""));
    }
  };

  const onApprove = async (row) => {
    await onUpdateEstado(row.id, 5);
    onRefresh?.();
  };

  const onNext = async (row) => {
    await onUpdateEstado(row.id, nextEstado(row.estado));
  };

  const onPrev = async (row) => {
    await onUpdateEstado(row.id, prevEstado(row.estado));
  };

  const onReject = async (row) => {
    await onUpdateEstado(row.id, 6);
  };

  const onDownload = (a) => {
    // abre a rota download (o browser trata o download)
    window.open(a.download_url, "_blank");
  };

  const baseColumns = useMemo(
    () => columnsFactory(onView, onPrev, onNext, onApprove, onReject),
    [onView, onPrev, onNext, onApprove, onReject]
  );

  const renderTable = (rows) => (
    <Table
      className="table-striped"
      style={{ overflowX: "auto" }}
      columns={baseColumns}
      dataSource={rows}
      rowKey={(r) => r.id}
      pagination={{ pageSize: 10 }}
    />
  );

  const items = [
    {
      key: "Recebidas",
      label: (
        <>
          Recebidas <Badge count={data.Recebidas.length} />
        </>
      ),
      children: renderTable(data.Recebidas),
    },
    {
      key: "Triagem",
      label: (
        <>
          Triagem <Badge count={data.Triagem.length} />
        </>
      ),
      children: renderTable(data.Triagem),
    },
    {
      key: "Entrevista",
      label: (
        <>
          Entrevista <Badge count={data.Entrevista.length} />
        </>
      ),
      children: renderTable(data.Entrevista),
    },
    {
      key: "Documentos",
      label: (
        <>
          Documentos <Badge count={data.Documentos.length} />
        </>
      ),
      children: renderTable(data.Documentos),
    },
    {
      key: "Aprovadas",
      label: (
        <>
          Aprovadas <Badge count={data.Aprovadas.length} />
        </>
      ),
      children: renderTable(data.Aprovadas),
    },
    {
      key: "Reprovadas",
      label: (
        <>
          Reprovadas <Badge count={data.Reprovadas.length} />
        </>
      ),
      children: renderTable(data.Reprovadas),
    },
  ];

  return (
    <>
      <Tabs defaultActiveKey="Recebidas" items={items} />
      <DetalheDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        cand={selected}
        anexos={anexos}
        onDownload={onDownload}
        onExportPdf={onExportPdf}
      />
    </>
  );
}
